import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";
import { DirectionalHint } from "@/components/fluent-tooltip-compat";

/**
 * Drop-in shim for Fluent UI's `Callout`. Renders a portal-positioned panel
 * anchored to `target`, with simple click-outside / Escape dismissal. No
 * radix Popover here because Fluent call sites already manage the open
 * state and anchor element themselves — we just need to portal next to a
 * DOM node when the call site decides we should be visible.
 *
 * `directionalHint` placement covers the eight Fluent positions we actually
 * use; the calculation is intentionally simple (no automatic flipping when
 * the popover would overflow the viewport — Fluent's default is similarly
 * naive in many places).
 */

// Fluent's `target` is permissive — it accepts `Element`, an `HTMLElement`,
// any `RefObject`, a CSS selector string, or even `MouseEvent`. We mirror
// that shape so call sites passing `document.activeElement`
// (Element | null), a `useRef<HTMLDivElement>(null)`, or a string selector
// all type-check.
export type FluentCalloutTarget =
  | Element
  | null
  | { current: Element | null }
  | string;

export interface FluentCalloutProps {
  target?: FluentCalloutTarget;
  onDismiss?: (ev?: any) => void;
  directionalHint?: number;
  gapSpace?: number;
  isBeakVisible?: boolean;
  beakWidth?: number;
  hidden?: boolean;
  className?: string;
  style?: React.CSSProperties;
  setInitialFocus?: boolean;
  preventDismissOnLostFocus?: boolean;
  preventDismissOnScroll?: boolean;
  preventDismissOnResize?: boolean;
  doNotLayer?: boolean;
  layerProps?: any;
  /** Drops */
  styles?: any;
  componentRef?: any;
  theme?: any;
  ariaLabel?: string;
  role?: string;
  children?: React.ReactNode;
}

function resolveTargetElement(t?: FluentCalloutTarget): Element | null {
  if (!t) return null;
  if (typeof t === "string") {
    return document.querySelector(t);
  }
  if (t instanceof Element) return t;
  if ("current" in t) return t.current ?? null;
  return null;
}

interface Position {
  top: number;
  left: number;
}

function computePosition(
  target: Element,
  rect: DOMRect,
  panel: HTMLElement | null,
  hint: number | undefined,
  gap: number,
): Position {
  const panelRect = panel?.getBoundingClientRect();
  const pw = panelRect?.width ?? 0;
  const ph = panelRect?.height ?? 0;

  switch (hint) {
    case DirectionalHint.topLeftEdge:
      return { top: rect.top - ph - gap, left: rect.left };
    case DirectionalHint.topCenter:
    case DirectionalHint.topAutoEdge:
      return { top: rect.top - ph - gap, left: rect.left + (rect.width - pw) / 2 };
    case DirectionalHint.topRightEdge:
      return { top: rect.top - ph - gap, left: rect.right - pw };
    case DirectionalHint.bottomCenter:
    case DirectionalHint.bottomAutoEdge:
      return { top: rect.bottom + gap, left: rect.left + (rect.width - pw) / 2 };
    case DirectionalHint.bottomRightEdge:
      return { top: rect.bottom + gap, left: rect.right - pw };
    case DirectionalHint.leftTopEdge:
      return { top: rect.top, left: rect.left - pw - gap };
    case DirectionalHint.leftCenter:
      return { top: rect.top + (rect.height - ph) / 2, left: rect.left - pw - gap };
    case DirectionalHint.leftBottomEdge:
      return { top: rect.bottom - ph, left: rect.left - pw - gap };
    case DirectionalHint.rightTopEdge:
      return { top: rect.top, left: rect.right + gap };
    case DirectionalHint.rightCenter:
      return { top: rect.top + (rect.height - ph) / 2, left: rect.right + gap };
    case DirectionalHint.rightBottomEdge:
      return { top: rect.bottom - ph, left: rect.right + gap };
    case DirectionalHint.bottomLeftEdge:
    default:
      return { top: rect.bottom + gap, left: rect.left };
  }
}

export const Callout: React.FC<FluentCalloutProps> = (props) => {
  const {
    target,
    onDismiss,
    directionalHint = DirectionalHint.bottomLeftEdge,
    gapSpace = 0,
    hidden,
    className,
    style,
    preventDismissOnLostFocus,
    preventDismissOnScroll,
    preventDismissOnResize,
    setInitialFocus,
    role,
    ariaLabel,
    children,
  } = props;

  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = React.useState<Position | null>(null);

  // Reposition on mount and on scroll/resize (unless dismissed instead).
  const reposition = React.useCallback(() => {
    const targetEl = resolveTargetElement(target);
    if (!targetEl) return;
    const rect = targetEl.getBoundingClientRect();
    const next = computePosition(
      targetEl,
      rect,
      panelRef.current,
      directionalHint,
      gapSpace,
    );
    // Clamp the panel inside the viewport so a menu anchored to a
    // top-right element doesn't extend past the right (or bottom) edge.
    const panelRect = panelRef.current?.getBoundingClientRect();
    const pw = panelRect?.width ?? 0;
    const ph = panelRect?.height ?? 0;
    const margin = 8;
    const vw =
      typeof window !== "undefined" ? window.innerWidth : Number.MAX_SAFE_INTEGER;
    const vh =
      typeof window !== "undefined" ? window.innerHeight : Number.MAX_SAFE_INTEGER;
    next.left = Math.max(margin, Math.min(next.left, vw - pw - margin));
    next.top = Math.max(margin, Math.min(next.top, vh - ph - margin));
    setPos(next);
  }, [target, directionalHint, gapSpace]);

  React.useLayoutEffect(() => {
    if (hidden) return;
    reposition();
    // Run again after first paint so we capture the panel's measured size.
    const id = requestAnimationFrame(reposition);
    return () => cancelAnimationFrame(id);
  }, [hidden, reposition]);

  // Click-outside / Escape / scroll / resize dismissals.
  React.useEffect(() => {
    if (hidden || !onDismiss) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (preventDismissOnLostFocus) return;
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        const targetEl = resolveTargetElement(target);
        if (targetEl && targetEl.contains(e.target as Node)) return;
        onDismiss();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss(e);
    };
    const handleScroll = () => {
      if (preventDismissOnScroll) {
        reposition();
      } else {
        onDismiss();
      }
    };
    const handleResize = () => {
      if (preventDismissOnResize) {
        reposition();
      } else {
        onDismiss();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [
    hidden,
    onDismiss,
    target,
    preventDismissOnLostFocus,
    preventDismissOnScroll,
    preventDismissOnResize,
    reposition,
  ]);

  // setInitialFocus: focus the first focusable element inside the panel.
  React.useEffect(() => {
    if (hidden || !setInitialFocus) return;
    const focusable = panelRef.current?.querySelector<HTMLElement>(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
    );
    focusable?.focus();
  }, [hidden, setInitialFocus]);

  if (hidden || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={panelRef}
      role={role ?? "dialog"}
      aria-label={ariaLabel}
      className={cn(
        // Portals to body, so it must outrank dialogs to work inside one — see ui/dialog.tsx.
        "fixed z-[10100] rounded-md border bg-popover p-2 text-popover-foreground shadow-md",
        // Hide briefly until first reposition so the panel doesn't flash at 0,0.
        pos == null && "invisible",
        className,
      )}
      style={{
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        ...style,
      }}
    >
      {children}
    </div>,
    document.body,
  );
};
Callout.displayName = "Callout";
